import { z } from 'zod';
import {
  alternativeSchema,
  confidenceLevels,
  findingSchema,
  metricShapeSchema,
  retiredReasons,
} from './finding-dto';
import { referenceKinds } from './references';

export const runUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  // With prompt caching on, Bedrock reports inputTokens as the uncached remainder and bills the cached
  // prefix through these separate counters. Optional so runs stored before caching stay valid on load.
  cacheReadInputTokens: z.number().int().nonnegative().optional(),
  cacheWriteInputTokens: z.number().int().nonnegative().optional(),
});

/**
 * One pipeline pass's token usage (authoring, grounding, repair, reconcile, revoice, actionability).
 * `calls` is that pass's LLM-call count — for the authoring pass, its iteration count. Summed across
 * passes it equals the run's `usage`. See `usage-meter.ts`.
 */
export const passUsageSchema = runUsageSchema.extend({
  calls: z.number().int().nonnegative(),
});

/**
 * The grounding critic's verdict per finding id. Recorded, never used to drop a finding — see the
 * note in `critics/review-findings.ts` on why this POC departs from SIR's fail-closed gate.
 */
export const groundingVerdictSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('grounded') }),
  z.object({
    kind: z.literal('unsupported'),
    claims: z.array(z.string()),
    /**
     * Whether the finding's central claim — the fact in its headline together with its metric — is
     * itself supported, judged apart from the peripheral `claims`. A grounded core carrying only
     * peripheral unsupported claims earns a Medium ceiling rather than Low (see
     * critics/derive-confidence.ts). Optional so verdicts written before this field still parse;
     * absent reads as "core not affirmed", preserving the earlier drop-to-Low behaviour.
     */
    coreSupported: z.boolean().optional(),
  }),
  z.object({ kind: z.literal('unverified'), reason: z.object({ code: z.string() }).loose() }),
]);

/**
 * The reconciliation critic's verdict per finding id. Independent of grounding by design: grounding
 * checks a finding against the evidence the AGENT itself gathered (closed-world — a methodologically
 * wrong number still grades "supported" when its arithmetic traces to that evidence), whereas
 * reconciliation re-derives the aggregate from a source of truth the agent did not select and flags
 * a finding whose claim contradicts it. Recorded on the run; a `contradicts` also caps the finding's
 * confidence to Low (see critics/derive-confidence.ts). `not_comparable` is the default when the
 * finding makes no claim the independent aggregate bears on, or the aggregate could not be computed.
 */
export const reconciliationVerdictSchema = z.object({
  agreement: z.enum(['agrees', 'contradicts', 'not_comparable']),
  note: z.string(),
});

export const reconciliationReviewSchema = z.object({
  verdicts: z.record(z.string(), reconciliationVerdictSchema),
  usage: runUsageSchema,
  reviewed: z.number().int().nonnegative(),
  /** The independently-computed aggregates the judge compared the findings against; kept for audit. */
  groundTruth: z.string().optional(),
});

/** Kept with its kind: `[item:46]` and `[objective:46]` are different references. */
export const unresolvedReferenceSchema = z.object({
  kind: z.enum(referenceKinds),
  id: z.number().int().nonnegative(),
});

/**
 * The reference critic's verdict per finding id. Deterministic, unlike grounding and reconciliation:
 * a verdict here is a fact about the run's tool output, not an opinion about its prose.
 */
export const referenceVerdictSchema = z.object({
  /** Fabricated: absent from every delivered tool result. Stripped from the text. */
  unresolved: z.array(unresolvedReferenceSchema),
  /** Present in a payload but not under a known id key: a gap in the registry, not a bad finding. */
  weak: z.array(unresolvedReferenceSchema),
  /** True when the finding was discarded because a claim-bearing field cited a fabricated id. */
  dropped: z.boolean(),
  /** Asserted blocker relations no tool result supports. Caps confidence; never drops. */
  unsupportedRelations: z.array(z.object({ itemId: z.number(), blockerId: z.number() })).optional(),
});

/**
 * The claimed-value critic's record. Deterministic like the reference critic, and one level deeper:
 * references prove an entity exists, this proves the state claimed for it is the state the tools
 * reported. `unread` is not evidence of invention — the level may come from a payload shape the
 * ledger does not parse — so it is recorded and never acted on.
 */
export const claimedValueMismatchSchema = z.object({
  findingId: z.string(),
  kind: z.enum(referenceKinds),
  id: z.number(),
  /** Every level the finding paired with this entity; for a contradiction, none of them matched. */
  claimed: z.array(z.string()),
  observed: z.array(z.string()),
});

export const claimedValueReviewSchema = z.object({
  contradicted: z.array(claimedValueMismatchSchema),
  /** Findings discarded because a contradicted claim sat in a field the reader acts on. */
  discarded: z.array(z.string()),
  unread: z.array(claimedValueMismatchSchema),
  /** Findings examined. Absent on runs written before the field existed. */
  reviewed: z.number().int().nonnegative().optional(),
  /**
   * Claims the ledger held an observed level for, so a contradiction was findable at all. `unread`
   * is the complement, and an empty `contradicted` over zero comparable claims is the check not
   * running rather than the run passing it.
   */
  comparable: z.number().int().nonnegative().optional(),
});

/**
 * The claimed-count critic's record: a count the run's own census contradicts. Caps confidence
 * rather than dropping, since a wrong denominator makes the number false while the finding under it
 * can still be true.
 */
export const countVerdictSchema = z.object({
  claimed: z.number(),
  expected: z.number(),
  part: z.enum(['numerator', 'denominator']),
  subject: z.literal('keyResults'),
});

export const countReviewSchema = z.object({
  verdicts: z.record(z.string(), countVerdictSchema),
  reviewed: z.number().int().nonnegative(),
  /**
   * Of `reviewed`, the findings this check had a number to re-derive — a key-result metric it could
   * parse, scoped to objectives a census the run actually read covers. Zero verdicts over zero
   * comparable findings is the check not running, which reads identically to a clean pass without
   * this. Absent on runs written before the field existed.
   */
  comparable: z.number().int().nonnegative().optional(),
});

/**
 * Which of the loop's objectives the run actually asked about. A run that stops early looks
 * identical to one with less to report — same shape, same confidence, fewer findings — so the
 * distinction is recorded rather than inferred. Counted from call arguments: an objective the run
 * merely listed is not one it examined.
 */
export const objectiveCoverageSchema = z.object({
  /** Objectives the loop holds, from a loop-scoped `list_objectives`. */
  inLoop: z.number().int().nonnegative(),
  examined: z.array(z.number()),
  unexamined: z.array(z.number()),
  /**
   * Present when the coverage gate sent the run back for more work, and absent when it had nothing
   * to say. `examined` above is the state the run FINISHED in, so a gate that worked leaves no trace
   * in it — the run reads exactly like one that never needed prompting. This is that trace: what the
   * gate named, and what coverage looked like when it fired. Comparing `examinedBefore` with
   * `examined` is what says whether the prompt moved anything.
   */
  prompted: z
    .object({
      objectives: z.array(z.number()),
      examinedBefore: z.array(z.number()),
    })
    .optional(),
});

/**
 * One claim a pass found and grounded, before anyone decides whether a reader should meet it.
 *
 * Deliberately not a finding: no prose, no recommendations, no evidence block, no confidence. A
 * candidate is one claim plus the calls behind it — all the authoring pass needs to choose from, at
 * a fraction of a finding's cost to produce.
 *
 * This is the only place a run records what it FOUND as opposed to what it SAID. Everything else
 * describes the chosen set, so "found and did not report" has no denominator without it.
 */
export const findingCandidateSchema = z.object({
  id: z.string().min(1),
  claim: z.string().min(1),
  /** Which pass raised it: an objective id as a string, or `loop` for the portfolio-wide pass. */
  source: z.string().min(1),
  objectiveIds: z.array(z.string()).default([]),
  /** The tool calls and ids behind the claim, in the same syntax a finding's `observed` uses. */
  observed: z.string().optional(),
  /**
   * What else could explain the same numbers, and whether the data rules it out — the same shape a
   * finding carries, tested HERE because this is where the tools are.
   *
   * `derive-confidence` reads a finding's alternatives directly: one left unresolved that would
   * invalidate the finding caps it at Unverified, one that only moves the size caps it at Medium. An
   * authoring pass with no tools can eliminate nothing, so without this every finding a fanned run
   * produced would cap at Medium or below — a confidence collapse caused purely by where the work
   * was done. The pass that gathered the evidence is the one that can test against it.
   */
  alternatives: z.array(alternativeSchema).default([]),
  /**
   * The pass's own pointer at the one tool result its primary figure came from, before code resolves
   * it into a full `groundingFactQuerySchema`. `factRef` names a `[[fact:N]]` marker the pass actually
   * saw — never a tool name or args the model transcribed itself.
   */
  groundingRef: z
    .object({
      factRef: z.number().int().positive(),
      metricShape: metricShapeSchema,
    })
    .optional(),
});

/**
 * The shadow arm of the grounding comparison: the same findings judged against only the evidence of
 * the passes that produced their candidates, instead of against the whole transcript.
 *
 * Recorded, never applied — `derive-confidence` reads the authoritative review and not this one.
 * It is here to be compared with, and `agreement.differences` is the actual output: a run where the
 * two arms disagree is one worth reading, and a few runs where they do not is the case for dropping
 * the expensive one.
 */
export const scopedGroundingReviewSchema = z.object({
  verdicts: z.record(z.string(), groundingVerdictSchema),
  /** Finding id -> the passes whose evidence it was judged against, via its `candidateIds`. */
  scopes: z.record(z.string(), z.array(z.string())),
  usage: runUsageSchema,
  reviewed: z.number().int().nonnegative(),
  agreement: z.object({
    compared: z.number().int().nonnegative(),
    agreed: z.number().int().nonnegative(),
    differences: z.array(
      z.object({
        findingId: z.string(),
        merged: z.string(),
        scoped: z.string(),
      }),
    ),
  }),
});

export const referenceReviewSchema = z.object({
  verdicts: z.record(z.string(), referenceVerdictSchema),
  /** Findings examined, including any subsequently dropped. */
  reviewed: z.number().int().nonnegative(),
  /** Discarded for a fabricated reference in a claim-bearing field. */
  dropped: z.number().int().nonnegative(),
  /** Zero means the check was vacuous, not that it passed. */
  observed: z
    .object({
      items: z.number().int().nonnegative(),
      objectives: z.number().int().nonnegative(),
    })
    .optional(),
});

export const groundingReviewSchema = z.object({
  verdicts: z.record(z.string(), groundingVerdictSchema),
  usage: runUsageSchema,
  reviewed: z.number().int().nonnegative(),
  /**
   * Whether the judge saw the whole transcript. Persisted because the first live run's
   * 0-grounded-of-6 verdict was an artefact of a starved evidence budget, not of bad claims —
   * a stored verdict without this is not interpretable later.
   */
  evidence: z
    .object({
      entryCount: z.number().int().nonnegative(),
      totalChars: z.number().int().nonnegative(),
      renderedChars: z.number().int().nonnegative(),
      truncatedEntries: z.number().int().nonnegative(),
      /** Allocations too small to read. Worse than truncated — the claim had almost no evidence. */
      starvedEntries: z.number().int().nonnegative().optional(),
      /** Compare against the budget to see whether it was anywhere near binding. */
      largestEntryChars: z.number().int().nonnegative().optional(),
    })
    .optional(),
});

/**
 * Bumped whenever the stored `Run` shape changes in a way older readers can't parse. Stamped on
 * every write and checked on read: rows carrying any other version are skipped, not migrated (see
 * `parseRun` in store/db-store.ts). Numeric to match the repo precedent in roadmap's
 * view-snapshot serializer (`CURRENT_SCHEMA_VERSION`).
 *
 * 1 -> 2: adds the lifecycle columns (stage, heartbeatAt, attempt, progress, failure, partial). No
 * migration — every version-1 row is inert (see the `status` column default in the schema) and
 * simply stops appearing once this ships. POC; the pass gets re-run.
 */
export const CURRENT_RUN_SCHEMA_VERSION = 2 as const;

export const runStatuses = ['running', 'completed', 'failed'] as const;
export const runStatusSchema = z.enum(runStatuses);

export const runStages = [
  'authoring',
  'grounding',
  'repair',
  'plainVoice',
  'reconcile',
  'revoice',
  'actionability',
  'deliveryRisk',
] as const;
export const runStageSchema = z.enum(runStages);

/**
 * One team's contribution to an objective, with the live conditions measured on that team. The team
 * is named as well as identified: a signal carries its team only as `scopeKey`, a bare id, and
 * nothing useful can be said to a reader about "team 84546".
 */
export const deliveryRiskTeamSchema = z.object({
  id: z.number().int(),
  name: z.string().min(1),
  openItems: z.number().int().nonnegative(),
  /** 0..1 — this team's share of the objective's open items. The materiality test. */
  shareOfOpenItems: z.number().min(0).max(1),
  signals: z.array(
    z.object({
      signalType: z.string().min(1),
      verdict: z.string().min(1),
      severity: z.string().optional(),
      /** Surfaced so a reader can see how old the reading is; a stale pass is otherwise invisible. */
      computedAt: z.string(),
    }),
  ),
});

/**
 * EXPERIMENTAL. A delivery condition measured on a team, paired with how much of an objective that
 * team carries — the one pairing neither source makes alone, since signals are scoped to org / loop /
 * team and never to an objective.
 *
 * NOT a statement about why the objective is behind, and must not be presented as one. Scope growth,
 * moved dates, inflated estimates and a wrong original baseline are all invisible to the pass that
 * produces this (there is no objective scope on a signal, and no historical objective read), so the
 * one visible candidate would otherwise win by default. `confidence` is capped at Medium in code for
 * the same reason. See `delivery-risk.ts`.
 *
 * Deliberately NOT a `Finding` and deliberately not in `runSchema.findings`: it is unvalidated, it
 * is rendered in its own section, and keeping it out means grouping, counts, grounding,
 * reconciliation and actionability are all untouched by the experiment.
 *
 * `hypothesis` is a correlation stated as a correlation. `wouldRefute` is required rather than
 * optional: a causal-sounding claim with no stated way to disprove it is exactly the
 * confident-garbage failure this pass has to avoid. `explainsFindingId` marks a related finding, not
 * a cause of one — the field name predates the framing and is kept to avoid churning stored runs.
 */
export const deliveryRiskSchema = z.object({
  objectiveId: z.string().min(1),
  objectiveName: z.string().min(1),
  headline: z.string().min(1),
  hypothesis: z.string().min(1),
  wouldRefute: z.string().min(1),
  confidence: z.enum(confidenceLevels),
  /** Set when this explains a finding the authoring pass already made. */
  explainsFindingId: z.string().optional(),
  teams: z.array(deliveryRiskTeamSchema).min(1),
});
export type DeliveryRisk = z.infer<typeof deliveryRiskSchema>;
export type DeliveryRiskTeam = z.infer<typeof deliveryRiskTeamSchema>;

/**
 * How wide a net the delivery-risk pass cast. Stored and rendered because the pass is bounded on two
 * axes (teams that fired, objectives in the loop) and an empty result is otherwise ambiguous: "no
 * team carrying this portfolio has a live condition" and "we joined ten of twenty-five firing teams
 * and none matched" look identical, and only the first means the portfolio is healthy.
 */
export const deliveryRiskCoverageSchema = z.object({
  teamsFiring: z.number().int().nonnegative(),
  /** Below `teamsFiring` means the join was partial by construction, not that nothing was found. */
  teamsConsidered: z.number().int().nonnegative(),
  objectivesTotal: z.number().int().nonnegative(),
  objectivesConsidered: z.number().int().nonnegative(),
  /** Signal types whose read filled its budget, so `teamsFiring` itself undercounts for them. */
  truncatedSignalTypes: z.array(z.string()),
  /**
   * Per objective that cleared the open-items floor, the strongest team share the join measured —
   * INCLUDING when it failed the 30% bar. Without this a zero-candidate run is silent about whether
   * it missed by a point or by an order of magnitude, which is the difference between "the bar is
   * slightly too high" and "no team carries an objective in this portfolio at all". Bounded by the
   * objective cap. Optional so stored runs written before this existed still parse.
   */
  bestShares: z
    .array(
      z.object({
        objectiveId: z.number().int(),
        objectiveOpenItems: z.number().int().nonnegative(),
        /**
         * Of `objectiveOpenItems`, how many carry no team at all. The denominator counts them while
         * no team's numerator can, so this is the headroom the bar is actually measured against —
         * the difference between "lower the bar" and "measure the share of teamed work instead".
         */
        objectiveTeamlessItems: z.number().int().nonnegative(),
        teamId: z.number().int(),
        teamOpenItems: z.number().int().nonnegative(),
        share: z.number(),
      }),
    )
    .optional(),
  /**
   * Wall clock of candidate generation, and the sequential round trips it waited on. Neither is an
   * LLM call, so nothing else in the run times them. `roundTrips` is what wall clock scales with —
   * the per-objective reads are sequential while the team slices inside one objective overlap — so
   * `elapsedMs / roundTrips` extrapolates a small loop to the objective cap. Optional for the same
   * reason as `bestShares`.
   */
  elapsedMs: z.number().int().nonnegative().optional(),
  roundTrips: z.number().int().nonnegative().optional(),
  /**
   * Candidate generation stopped on its own wall clock rather than covering every objective. Kept
   * apart from `objectivesConsidered < objectivesTotal`, which is also true when the objective cap
   * bit: the two ask for opposite fixes — raise the cap, or find out why the store is slow.
   */
  deadlineHit: z.boolean().optional(),
});
export type DeliveryRiskCoverage = z.infer<typeof deliveryRiskCoverageSchema>;

export const runFailureCodes = [
  'noRolesConfigured',
  'noLlmProvider',
  'providerUnavailable',
  'dataUnavailable',
  'maxIterations',
  'contextLimit',
  'enqueueFailed',
  'orphaned',
  'supersededByActiveRun',
  'attemptsExhausted',
  'lifetimeExceeded',
  'internal',
] as const;
export const runFailureCodeSchema = z.enum(runFailureCodes);

export const runFailureSchema = z.object({
  code: runFailureCodeSchema.catch('internal'),
  /** Operator-facing detail, expert mode only. Never shown to a reader by default. */
  detail: z.string().optional(),
});

export const runProgressSchema = z.object({
  // Carries its own stage (rather than being cleared at every pass boundary) so a stale note from
  // the previous pass is detectable and can be suppressed instead of misattributed — see
  // `RunProgressLine`.
  stage: runStageSchema.catch('authoring'),
  turn: z.number().int().positive(),
  tools: z.array(z.string()),
  /** ~200 chars, truncated at the write site. */
  note: z.string().optional(),
  at: z.string(),
});

/**
 * Two clocks: `heartbeatAt ?? created_at` bounds a run that may never have started; `started_at`
 * bounds how long one that has started may take. Absent -> dead would fail every run the instant it
 * is triggered; absent -> alive would make a queued row immortal.
 */

/** A run whose worker has been silent this long reads as orphaned. Display + poll only. */
export const RUN_HEARTBEAT_STALE_AFTER_MS = 5 * 60 * 1000;

/**
 * How long a run must be silent before anything is allowed to KILL it (settle it failed / claim it
 * out from under the previous attempt). Destructive paths only: the worker boot sweep, the route's
 * sweep-before-insert, and the consumer's cross-id takeover (Rule B). Longer than the display window
 * on purpose — "this looks dead" at 5 minutes and "actually kill it" at 15 is the right asymmetry,
 * because the second is irreversible and costs real money.
 */
export const RUN_TAKEOVER_AFTER_MS = 15 * 60 * 1000;

/**
 * How long a run may wait to be picked up. Matched to `sir_agentic_run`'s
 * `message_retention_seconds` (nexus-infra `modules/services/sir.tf`) — past that SQS has dropped
 * the message, so nothing can ever claim the row. Move it with that retention. Provisional: sized
 * from the queue, not from a measured drain.
 */
export const RUN_NO_HEARTBEAT_GRACE_MS = 6 * 60 * 60 * 1000;

/**
 * Total wall-clock a run may spend executing across every attempt combined, measured from
 * `started_at` and checked before any claim regardless of remaining attempt budget. Raising
 * `MAX_ATTEMPTS` bounds how many times a run can die, not how long it can take. A row with no
 * `started_at` has never been claimed, so this does not apply to it yet.
 */
export const RUN_MAX_LIFETIME_MS = 2 * 60 * 60 * 1000;

/**
 * Optional cost/latency diagnostics captured per run. Exists to compare data-acquisition strategies
 * — e.g. the agent deriving delivery-risk conditions from raw item/health tools vs reading SIR's
 * precomputed signals. Every field is additive and optional so runs written before it still parse;
 * this is internal instrumentation, never rendered to customers.
 */
export const perToolUsageSchema = z.object({
  tool: z.string(),
  calls: z.number().int().nonnegative(),
  /**
   * Rendered evidence-ledger chars this tool contributed across the run (header + result text),
   * summing to `grounding.evidence.totalChars` — the payload-size measure for the raw-vs-signals table.
   */
  chars: z.number().int().nonnegative(),
});

export const runInstrumentationSchema = z.object({
  /** Wall-clock latency of the whole pipeline, and of the authoring agent loop alone. */
  latencyMs: z
    .object({ totalMs: z.number().nonnegative(), authoringMs: z.number().nonnegative() })
    .optional(),
  /** Token usage split by pipeline pass — where a run's tokens actually go. */
  perPassUsage: z.record(z.string(), passUsageSchema).optional(),
  /** Per-tool call count and rendered payload size the authoring agent ingested. */
  perTool: z.array(perToolUsageSchema).optional(),
});

/**
 * How wide a net the authoring loop casts for signals: `curated` = the loop-relevant default set,
 * `all` = the full catalog. Chosen per run at trigger time.
 */
/**
 * A standing finding the run did not carry forward. `reason` is the writer's when it declared the
 * retirement, otherwise code's: `superseded` when a finding this run shares its subject without
 * continuing it, `not_observed` when nothing in this run speaks to it at all.
 */
export const retiredFindingSchema = z.object({
  id: z.string().min(1),
  headline: z.string(),
  objectiveIds: z.array(z.string()).default([]),
  reason: z.enum(retiredReasons),
  note: z.string().optional(),
  lastSeenRunId: z.string().min(1),
  lastSeenAt: z.string().optional(),
  decidedBy: z.enum(['model', 'code']),
});

/** Which earlier runs fed the standing set this run was written against. */
export const standingSummarySchema = z.object({
  runIds: z.array(z.string()),
  findings: z.number().int().nonnegative(),
});

export const signalScopeSchema = z.enum(['curated', 'all']);
export type SignalScope = z.infer<typeof signalScopeSchema>;

/**
 * How the run used its computed rank. `shadow` = scored and stored, order untouched (the writer's
 * `decisionRank` still decides); `order` = the score decided the cap and the stored order. The env
 * dial's third value, off, stores nothing here.
 */
export const rankingModes = ['shadow', 'order'] as const;
export const rankingModeSchema = z.enum(rankingModes);
export const runRankingSchema = z.object({
  version: z.number().int().positive(),
  mode: rankingModeSchema,
});
export type RankingMode = (typeof rankingModes)[number];
export type RunRanking = z.infer<typeof runRankingSchema>;

export const runSchema = z.object({
  version: z.literal(CURRENT_RUN_SCHEMA_VERSION),
  // Do not add a ULID format regex here even though §5.3 mints one going forward: a stored run is
  // matched by the version gate, not by its id shape, and a format check would push a differently-
  // shaped (but validly versioned) id through parseRun's skip path instead of surfacing a real bug.
  runId: z.string().min(1),
  organizationId: z.string().min(1),
  /**
   * The loop a run is scoped to. Optional because a run can be org-wide — objectives are org-level
   * and link items across loops, so insights for one objective span every loop. Absent means the
   * run is not tied to a single loop; the store keys a run on `(organizationId, runId)`.
   */
  loopId: z.number().int().positive().optional(),
  personaId: z.string().min(1),
  generatedAt: z.string(),
  model: z.string(),
  /**
   * Lifecycle state. A run is created `running`, then flips to `completed` or `failed` when the
   * background loop settles. Optional so runs written before async execution (which had none) still
   * parse under version 1; readers treat an absent status as already finished.
   */
  status: runStatusSchema.optional(),
  /**
   * Where the run currently is (or died). `.catch(undefined)` matters for more than defensiveness:
   * an unrecognized stage string (a stage since removed) must degrade to "no stage" rather than fail
   * `runSchema.parse` for the whole response.
   */
  stage: runStageSchema.optional().catch(undefined),
  heartbeatAt: z.string().optional(),
  attempt: z.number().int().nonnegative().optional(),
  /** Latest turn only — not a history. */
  progress: runProgressSchema.optional(),
  /** Why a `failed` run stopped. Absent on a run that failed before this field existed. */
  failure: runFailureSchema.optional(),
  /**
   * True when `findings` were captured mid-pipeline rather than at a normal terminal write — i.e. the
   * run failed after producing some findings. Always paired with `status: 'failed'`.
   */
  partial: z.boolean().optional(),
  findings: z.array(findingSchema),
  toolsUsed: z.array(z.string()),
  iterations: z.number().int().nonnegative(),
  usage: runUsageSchema,
  grounding: groundingReviewSchema.optional(),
  /**
   * The reconciliation critic's cross-check of each finding against an independently recomputed
   * aggregate. Optional so runs written before this critic still parse under version 1.
   */
  reconciliation: reconciliationReviewSchema.optional(),
  /**
   * Optional so runs written before this critic still parse under version 1 — `getLatestRun`
   * re-validates the whole stored history, which a required field would make unreadable.
   */
  references: referenceReviewSchema.optional(),
  /**
   * The shadow grounding arm's verdicts. Persisted rather than only logged: its whole output is the
   * comparison with `grounding`, and a difference nobody can read back later is a call we paid for
   * and threw away. Absent on runs authored before the fan-out, and on any run where it failed.
   */
  scopedGrounding: scopedGroundingReviewSchema.optional(),
  /** Absent on runs written before the check existed; see `claimedValueReviewSchema`. */
  claimedValues: claimedValueReviewSchema.optional(),
  /** Absent on runs written before the check existed; see `countReviewSchema`. */
  counts: countReviewSchema.optional(),
  /** Absent when the run made no loop-scoped `list_objectives` call; see `objectiveCoverageSchema`. */
  objectiveCoverage: objectiveCoverageSchema.optional(),
  /**
   * Optional cost/latency diagnostics; see `runInstrumentationSchema`. Absent on older runs and on
   * the `running` stub — populated when the pipeline completes.
   */
  instrumentation: runInstrumentationSchema.optional(),
  /** Which signal breadth the run was authored with; recorded so Curated-vs-ALL runs are distinguishable. */
  signalScope: signalScopeSchema.optional(),
  /** Whether and how this run's findings carry a computed `rank`; see `runRankingSchema`. Absent = not scored. */
  ranking: runRankingSchema.optional(),
  /** Standing findings this run retired, with why. Absent before persistence existed. */
  retired: z.array(retiredFindingSchema).optional(),
  /** Every claim the passes raised, kept so the next run can reuse an unchanged objective's. */
  candidates: z.array(findingCandidateSchema).optional(),
  standing: standingSummarySchema.optional(),
  /**
   * EXPERIMENTAL, flag-gated, and separate from `findings` on purpose — see `deliveryRiskSchema`.
   * Optional so every run stored before this existed still parses.
   */
  deliveryRisk: z.array(deliveryRiskSchema).optional(),
  /**
   * What the delivery-risk pass looked at, written whenever the pass ran — including when it produced
   * nothing, which is the case the numbers exist to disambiguate. Absent means the pass did not run.
   */
  deliveryRiskCoverage: deliveryRiskCoverageSchema.optional(),
});

export const triggerRunRequestSchema = z.object({
  loopId: z.coerce.number().int().positive(),
});

export const triggerRunBodySchema = z.object({
  signalScope: signalScopeSchema.optional().default('curated'),
});
export type TriggerRunBody = z.infer<typeof triggerRunBodySchema>;

export const triggerRunResponseSchema = z.object({
  runId: z.string(),
  status: z.literal('running'),
});

/**
 * One row of a run list — enough for a picker to render without fetching every run in full.
 * `findingCount` isn't a stored field; it's derived from `findings.length` at read time, so this
 * schema is kept structurally independent of `runSchema` rather than `.pick()`ing from it.
 */
export const runSummarySchema = z.object({
  runId: z.string().min(1),
  generatedAt: z.string(),
  status: runStatusSchema.optional(),
  // Only the fields the picker badges a state with, not the ones that would explain one — expert
  // detail (stage, attempt, failure) belongs to the full `Run`, fetched once a row is selected.
  heartbeatAt: z.string().optional(),
  stage: runStageSchema.optional().catch(undefined),
  model: z.string(),
  findingCount: z.number().int().nonnegative(),
});

/**
 * The 409 body for `POST .../loops/:loopId/runs`, for either reason it can be held: the per-loop
 * interlock (`RUN_ALREADY_ACTIVE`, `meta.activeRunId` present) or no configured roles
 * (`NO_ROLES_CONFIGURED`, no `meta`). Returned explicitly via `c.json(..., 409)` rather than thrown
 * as a `ConflictError`, whose `{message, details}` rendering would collapse to `code: 'UNKNOWN_ERROR'`
 * on the frontend and lose `meta.activeRunId`. Shaped to match `tempoApiErrorWrapperSchema`'s first
 * union branch so `code` and `meta` both survive the round trip.
 */
export const runAlreadyActiveErrorSchema = z.object({
  code: z.enum(['RUN_ALREADY_ACTIVE', 'NO_ROLES_CONFIGURED']),
  title: z.string(),
  detail: z.string().optional(),
  meta: z.object({ activeRunId: z.string() }).optional(),
});

export type Run = z.infer<typeof runSchema>;
export type RunSummary = z.infer<typeof runSummarySchema>;
export type RunStatus = z.infer<typeof runStatusSchema>;
export type RunStage = z.infer<typeof runStageSchema>;
export type RunFailureCode = z.infer<typeof runFailureCodeSchema>;
export type RunFailure = z.infer<typeof runFailureSchema>;
export type RunProgress = z.infer<typeof runProgressSchema>;
export type RunUsage = z.infer<typeof runUsageSchema>;
export type PassUsage = z.infer<typeof passUsageSchema>;
export type PerToolUsage = z.infer<typeof perToolUsageSchema>;
export type RunInstrumentation = z.infer<typeof runInstrumentationSchema>;
export type GroundingVerdict = z.infer<typeof groundingVerdictSchema>;
export type GroundingReview = z.infer<typeof groundingReviewSchema>;
export type UnresolvedReference = z.infer<typeof unresolvedReferenceSchema>;
export type ReferenceVerdict = z.infer<typeof referenceVerdictSchema>;
export type ReferenceReview = z.infer<typeof referenceReviewSchema>;
export type ClaimedValueMismatch = z.infer<typeof claimedValueMismatchSchema>;
export type ClaimedValueReview = z.infer<typeof claimedValueReviewSchema>;
export type CountVerdict = z.infer<typeof countVerdictSchema>;
export type CountReview = z.infer<typeof countReviewSchema>;
export type ObjectiveCoverage = z.infer<typeof objectiveCoverageSchema>;
export type FindingCandidate = z.infer<typeof findingCandidateSchema>;
export type RetiredFinding = z.infer<typeof retiredFindingSchema>;
export type StandingSummary = z.infer<typeof standingSummarySchema>;
export type ScopedGroundingReview = z.infer<typeof scopedGroundingReviewSchema>;
export type ReconciliationVerdict = z.infer<typeof reconciliationVerdictSchema>;
export type ReconciliationReview = z.infer<typeof reconciliationReviewSchema>;
export type TriggerRunResponse = z.infer<typeof triggerRunResponseSchema>;
export type RunAlreadyActiveError = z.infer<typeof runAlreadyActiveErrorSchema>;
